import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, Check } from 'lucide-react';
import './DateRangeFilter.css';

const DATE_RANGES = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '1y', label: 'Last year' },
  { value: 'custom', label: 'Custom range' }
];

const DateRangeFilter = ({ value, onChange, customRange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [startDate, setStartDate] = useState(customRange?.startDate || '');
  const [endDate, setEndDate] = useState(customRange?.endDate || '');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCustomPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (rangeValue) => {
    if (rangeValue === 'custom') {
      setShowCustomPicker(true);
    } else {
      onChange(rangeValue);
      setIsOpen(false);
      setShowCustomPicker(false);
    }
  };

  const handleCustomApply = () => {
    if (startDate && endDate) {
      onChange('custom', { startDate, endDate });
      setIsOpen(false);
      setShowCustomPicker(false);
    }
  };

  const selectedRange = DATE_RANGES.find(r => r.value === value);
  const displayLabel = value === 'custom' && customRange?.startDate && customRange?.endDate
    ? `${formatDate(customRange.startDate)} - ${formatDate(customRange.endDate)}`
    : selectedRange?.label || 'Select range';

  return (
    <div className="date-range-filter" ref={dropdownRef}>
      <button
        className={`date-range-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Calendar className="trigger-icon" />
        <span className="trigger-label">{displayLabel}</span>
        <ChevronDown className={`trigger-chevron ${isOpen ? 'rotated' : ''}`} />
      </button>

      {isOpen && (
        <div className="date-range-dropdown">
          {!showCustomPicker ? (
            <ul className="range-options">
              {DATE_RANGES.map((range) => (
                <li key={range.value}>
                  <button
                    className={`range-option ${value === range.value ? 'selected' : ''}`}
                    onClick={() => handleSelect(range.value)}
                  >
                    <span>{range.label}</span>
                    {value === range.value && <Check className="check-icon" />}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="custom-picker">
              <h4 className="picker-title">Select date range</h4>
              <div className="picker-fields">
                <div className="picker-field">
                  <label>Start date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate || undefined}
                  />
                </div>
                <div className="picker-field">
                  <label>End date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate || undefined}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              <div className="picker-actions">
                <button
                  className="picker-cancel"
                  onClick={() => setShowCustomPicker(false)}
                >
                  Cancel
                </button>
                <button
                  className="picker-apply"
                  onClick={handleCustomApply}
                  disabled={!startDate || !endDate}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper function to format date
function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

export default DateRangeFilter;
